import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { ShieldCheck, CheckCircle2, XCircle, ArrowUpRight, Printer, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";

export default function Cert() {
  const { certNo } = useParams();
  const { branding } = useTheme();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/cert/${certNo}`)
      .then((r) => setCert(r.data))
      .catch(() => setErr("Certificate not found."));
  }, [certNo]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center max-w-md p-10">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="heading-display text-3xl mb-2">Certificate not found</h1>
          <p className="text-muted-foreground mb-6">The certificate number <code className="font-mono">{certNo}</code> doesn't match our records.</p>
          <Link to="/"><Button className="rounded-none">Back to {branding.platform_name}</Button></Link>
        </div>
      </div>
    );
  }
  if (!cert) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const approved = cert.decision === "approved";

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2" data-testid="cert-home-link">
            <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black">
              {(branding.platform_name || "C").slice(0, 1)}
            </div>
            <span className="font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>{branding.platform_name}</span>
          </Link>
          <div className="flex gap-2">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/cert/${certNo}.pdf`} target="_blank" rel="noopener noreferrer" data-testid="cert-pdf-download-btn">
              <Button className="rounded-none">
                <FileDown className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </a>
            <Button onClick={() => window.print()} variant="outline" className="rounded-none" data-testid="cert-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        <div className="border-2 border-foreground bg-background p-10 sm:p-16 relative overflow-hidden" data-testid="cert-card">
          <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
          <div className="relative">
            <div className="flex items-start justify-between mb-12">
              <div>
                <div className="label-eyebrow mb-2">Approval Certificate</div>
                <h1 className="heading-display text-2xl">{cert.platform_name}</h1>
              </div>
              <div className={approved ? "text-emerald-600" : "text-red-600"}>
                {approved ? <CheckCircle2 className="w-12 h-12" /> : <XCircle className="w-12 h-12" />}
              </div>
            </div>

            <div className="space-y-1 mb-12">
              <div className="label-eyebrow">{approved ? "Approved" : "Changes requested"}</div>
              <h2 className="heading-display text-4xl sm:text-5xl leading-tight" data-testid="cert-item-title">{cert.item_title}</h2>
              <p className="text-muted-foreground text-sm">Version v{cert.version_number} · {cert.item_type}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6 mb-12 text-sm">
              <Row label="Project" value={cert.project_name} />
              <Row label="Client" value={cert.client_name} />
              <Row label="Workspace" value={cert.workspace_name} />
              <Row label="Decision date" value={fmtDate(cert.created_at)} />
              <Row label="Reviewer" value={cert.reviewer_name} />
              <Row label="Reviewer email" value={cert.reviewer_email} />
            </div>

            {cert.comment && (
              <div className="border-l-2 border-primary pl-4 mb-12">
                <div className="label-eyebrow mb-1">Reviewer note</div>
                <p className="text-sm italic">"{cert.comment}"</p>
              </div>
            )}

            <div className="flex items-end justify-between border-t border-border pt-6">
              <div>
                <div className="label-eyebrow text-[9px] mb-1">Certificate number</div>
                <div className="font-mono text-lg font-bold" data-testid="cert-number">{cert.certificate_no}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow text-[9px] mb-1">Immutable record</div>
                <ShieldCheck className="w-5 h-5 inline text-primary" />
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground mb-3">
            Approval recorded on <span className="font-semibold">{cert.platform_name}</span>. Share this URL as proof to clients, in invoices, or portfolio case studies.
          </p>
          <Link to="/" className="inline-flex items-center text-xs text-primary hover:underline">
            Get your team on {cert.platform_name} <ArrowUpRight className="w-3 h-3 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow text-[9px] mb-1">{label}</div>
      <div className="font-semibold">{value || "—"}</div>
    </div>
  );
}
